import type {
  TplAdminCategory,
  TplAdminRow,
  TplCategory,
  TplEmail,
  TplJourney,
  TplJourneyStep,
  TplLoopStep,
} from './types';

/* ------------------------------------------------------ shared taxonomy --- */

/**
 * The admin-managed category taxonomy, shared across both Email and Journeys
 * surfaces. In product this is editable (dm_template_category) — here it is a
 * typed id + label list the views read counts from.
 */
export const TPL_CATEGORIES: TplCategory[] = [
  { id: 'promotions', label: 'Promotions' },
  { id: 'reminders', label: 'Treatment Reminders' },
  { id: 'newsletters', label: 'Newsletters' },
  { id: 'postop', label: 'Post-Op Care' },
  { id: 'recall', label: 'Recall / Reactivation' },
  { id: 'welcome', label: 'New-Patient Welcome' },
  { id: 'reviews', label: 'Review Requests' },
];

/* --------------------------------------------------------- email library --- */

export const TPL_EMAILS: TplEmail[] = [
  {
    id: 'em_whitening',
    name: 'Spring Whitening Promo',
    category: 'promotions',
    categoryLabel: 'Promotions',
    description:
      'A seasonal in-office whitening offer with the discount and expiry pulled from your offer-facts and a Book now button.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_benefits',
    name: 'Insurance Benefits Expiring',
    category: 'promotions',
    categoryLabel: 'Promotions',
    description:
      'A year-end nudge reminding patients to use remaining dental insurance benefits before they reset on Jan 1.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_recall6mo',
    name: '6-Month Recall Reminder',
    category: 'reminders',
    categoryLabel: 'Treatment Reminders',
    description:
      "A clean reminder email with the patient's last-visit date and a one-click Book now link for their hygiene check-up.",
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_confirm',
    name: 'Appointment Confirmation',
    category: 'reminders',
    categoryLabel: 'Treatment Reminders',
    description:
      'Confirms an upcoming visit with date, time, provider, and address — plus reschedule and add-to-calendar links.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_postop',
    name: 'Post-Op Care Instructions',
    category: 'postop',
    categoryLabel: 'Post-Op Care',
    description:
      "Recovery guidance after an extraction or surgery — what to expect, do's and don'ts, and a number to call if needed.",
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_missyou',
    name: 'We Miss You — Come Back',
    category: 'recall',
    categoryLabel: 'Recall / Reactivation',
    description:
      'A warm one-off email to patients with no visit in 18+ months, offering a complimentary exam to bring them back.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_reactivate',
    name: 'Reactivation Offer',
    category: 'recall',
    categoryLabel: 'Recall / Reactivation',
    description:
      'Re-engages lapsed patients with a limited-time returning-patient check-up and a clear booking call-to-action.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_welcome',
    name: 'New Patient Welcome',
    category: 'welcome',
    categoryLabel: 'New-Patient Welcome',
    description:
      'Greets new patients with your address, hours, parking, and a link to intake forms before their first appointment.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_newsletter',
    name: 'Monthly Practice Newsletter',
    category: 'newsletters',
    categoryLabel: 'Newsletters',
    description:
      'A multi-section monthly update with office news, oral-health tips, team highlights, and a seasonal offer block.',
    scope: 'global',
    channel: 'email',
  },
  {
    id: 'em_saved_whitening',
    name: 'Smile Dental — Whitening (edited)',
    category: 'promotions',
    categoryLabel: 'Saved · Promotions',
    description:
      "Your customized version of the whitening promo, with Smile Dental's pricing, hours, and brand voice locked in.",
    scope: 'org_private',
    channel: 'email',
  },
  {
    id: 'em_saved_welcome',
    name: 'Our Welcome Email v2',
    category: 'welcome',
    categoryLabel: 'Saved · New-Patient Welcome',
    description:
      "Front-desk's reworked welcome email with the new-patient packet link and Dr. Chen's signature already in place.",
    scope: 'org_private',
    channel: 'email',
  },
];

/* --------------------------------------------------------- journeys lib --- */

/** Build a Day/Wait timeline from a channel + wait-gap sequence. */
function buildSteps(seq: { channel: TplJourneyStep['channel']; wait: number }[]): TplJourneyStep[] {
  let day = 0;
  return seq.map((s, i) => {
    const waitDays = i === 0 ? 0 : s.wait;
    day += waitDays;
    return { day, waitDays, channel: s.channel };
  });
}

export const TPL_JOURNEYS: TplJourney[] = [
  {
    id: 'jr_recall6mo',
    name: '6-month hygiene recall',
    category: 'recall',
    categoryLabel: 'Recall / Reactivation',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 4 },
      { channel: 'sms', wait: 6 },
    ]),
  },
  {
    id: 'jr_winback',
    name: 'Win back lapsed patients',
    category: 'recall',
    categoryLabel: 'Recall / Reactivation',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 3 },
      { channel: 'email', wait: 4 },
      { channel: 'sms', wait: 7 },
    ]),
  },
  {
    id: 'jr_overdue',
    name: 'Overdue cleaning push',
    category: 'recall',
    categoryLabel: 'Recall / Reactivation',
    scope: 'global',
    steps: buildSteps([
      { channel: 'sms', wait: 0 },
      { channel: 'sms', wait: 5 },
    ]),
  },
  {
    id: 'jr_welcome_series',
    name: 'New-patient welcome series',
    category: 'welcome',
    categoryLabel: 'New Patient',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 2 },
      { channel: 'email', wait: 5 },
      { channel: 'sms', wait: 7 },
    ]),
  },
  {
    id: 'jr_firstvisit',
    name: 'First-visit onboarding',
    category: 'welcome',
    categoryLabel: 'New Patient',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 1 },
    ]),
  },
  {
    id: 'jr_postop',
    name: 'Post-op care follow-up',
    category: 'posttx',
    categoryLabel: 'Treatment Follow-up',
    scope: 'global',
    steps: buildSteps([
      { channel: 'sms', wait: 0 },
      { channel: 'email', wait: 3 },
      { channel: 'sms', wait: 4 },
    ]),
  },
  {
    id: 'jr_unscheduled',
    name: 'Unscheduled treatment reminder',
    category: 'posttx',
    categoryLabel: 'Treatment Follow-up',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'email', wait: 5 },
      { channel: 'sms', wait: 7 },
    ]),
  },
  {
    id: 'jr_implant',
    name: 'Implant consult promo series',
    category: 'promotions',
    categoryLabel: 'Promotions',
    scope: 'global',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'email', wait: 5 },
      { channel: 'sms', wait: 5 },
    ]),
  },
  {
    id: 'jr_review',
    name: 'Post-visit review request',
    category: 'reviews',
    categoryLabel: 'Reviews',
    scope: 'global',
    steps: buildSteps([
      { channel: 'sms', wait: 0 },
      { channel: 'email', wait: 4 },
    ]),
  },
  {
    id: 'jr_saved_invisalign',
    name: 'Invisalign nurture (our copy)',
    category: 'promotions',
    categoryLabel: 'Promotions',
    scope: 'org_private',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 4 },
      { channel: 'email', wait: 6 },
    ]),
  },
  {
    id: 'jr_saved_rewelcome',
    name: 'Returning-patient re-welcome',
    category: 'welcome',
    categoryLabel: 'New Patient',
    scope: 'org_private',
    steps: buildSteps([
      { channel: 'email', wait: 0 },
      { channel: 'sms', wait: 3 },
    ]),
  },
];

/** Journey-only categories (taxonomy id -> label), used for the sub-nav. */
export const TPL_JOURNEY_CATEGORIES: TplCategory[] = [
  { id: 'recall', label: 'Recall / Reactivation' },
  { id: 'welcome', label: 'New Patient' },
  { id: 'posttx', label: 'Treatment Follow-up' },
  { id: 'promotions', label: 'Promotions' },
  { id: 'reviews', label: 'Reviews' },
];

/* ------------------------------------------------------------- admin lib --- */

/**
 * The global library, in Admin mode (internal). Email surface shown; the
 * Journeys surface is symmetric (same table, same actions, same taxonomy).
 */
export const TPL_ADMIN_EMAIL_ROWS: TplAdminRow[] = [
  {
    id: 'adm_recall_6mo',
    name: '6-month hygiene recall',
    slug: 'tpl_recall_6mo',
    surface: 'email',
    category: 'reminders',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 28, 2026',
  },
  {
    id: 'adm_recall_email',
    name: 'Time for your check-up',
    slug: 'tpl_recall_email',
    surface: 'email',
    category: 'reminders',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 26, 2026',
  },
  {
    id: 'adm_welcome_email',
    name: 'Welcome to the practice',
    slug: 'tpl_welcome_email',
    surface: 'email',
    category: 'welcome',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 22, 2026',
  },
  {
    id: 'adm_promo_whitening',
    name: 'Spring whitening promo',
    slug: 'tpl_promo_whitening',
    surface: 'email',
    category: 'promotions',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 19, 2026',
  },
  {
    id: 'adm_review_email',
    name: 'How was your visit?',
    slug: 'tpl_review_email',
    surface: 'email',
    category: 'reviews',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 14, 2026',
  },
  {
    id: 'adm_reactivation_email',
    name: 'We miss you — come back',
    slug: 'tpl_reactivation_email',
    surface: 'email',
    category: 'recall',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 11, 2026',
  },
  {
    id: 'adm_promo_invisalign',
    name: 'Summer Invisalign event',
    slug: 'tpl_promo_invisalign',
    surface: 'email',
    category: 'promotions',
    publishState: 'draft',
    authoredBy: 'ELVA',
    lastEdited: 'Jun 16, 2026',
  },
  {
    id: 'adm_news_holiday',
    name: 'Holiday hours & closures',
    slug: 'tpl_news_holiday',
    surface: 'email',
    category: 'newsletters',
    publishState: 'draft',
    authoredBy: 'ELVA',
    lastEdited: 'Jun 12, 2026',
  },
  {
    id: 'adm_postop_email',
    name: 'Post-op care instructions',
    slug: 'tpl_postop_email',
    surface: 'email',
    category: 'postop',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'May 6, 2026',
  },
  {
    id: 'adm_referral_email',
    name: 'Refer a friend credit',
    slug: 'tpl_referral_email',
    surface: 'email',
    category: 'promotions',
    publishState: 'draft',
    authoredBy: 'ELVA',
    lastEdited: 'Jun 9, 2026',
  },
  {
    id: 'adm_welcome_forms',
    name: 'New-patient intake forms',
    slug: 'tpl_welcome_forms',
    surface: 'email',
    category: 'welcome',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'Apr 30, 2026',
  },
  {
    id: 'adm_news_quarterly',
    name: 'Quarterly newsletter',
    slug: 'tpl_news_quarterly',
    surface: 'email',
    category: 'newsletters',
    publishState: 'published',
    authoredBy: 'ELVA',
    lastEdited: 'Apr 24, 2026',
  },
];

/** Admin surface-switch counts: Email vs Journeys. */
export const TPL_ADMIN_SURFACE_COUNTS = { email: 12, journeys: 6 } as const;

/** Category-management panel rows (shared across Email + Journeys). */
export const TPL_ADMIN_CATEGORIES: TplAdminCategory[] = [
  { id: 'reminders', label: 'Treatment Reminders', count: 8 },
  { id: 'promotions', label: 'Promotions', count: 6 },
  { id: 'welcome', label: 'New-Patient Welcome', count: 4 },
  { id: 'recall', label: 'Reactivation', count: 3 },
  { id: 'reviews', label: 'Review Requests', count: 3 },
  { id: 'postop', label: 'Post-Op Care', count: 2 },
  { id: 'newsletters', label: 'Newsletters', count: 2 },
];

/** The curate -> publish content loop shown above the Admin table. */
export const TPL_ADMIN_LOOP: TplLoopStep[] = [
  { n: 1, label: 'Cowork research', tone: 'spec' },
  { n: 2, label: 'Curate' },
  { n: 3, label: 'Build in Admin mode', tone: 'mine' },
  { n: 4, label: 'Publish to global library', tone: 'mine' },
  { n: 5, label: 'Practices browse & launch' },
];

/* ----------------------------------------------------------- count utils --- */

/** Count global + saved email templates by category id. */
export function tplEmailCount(categoryId: string): number {
  if (categoryId === 'all') return TPL_EMAILS.length;
  if (categoryId === 'saved') return TPL_EMAILS.filter((t) => t.scope === 'org_private').length;
  return TPL_EMAILS.filter((t) => t.category === categoryId).length;
}

/** Count journeys by category id. */
export function tplJourneyCount(categoryId: string): number {
  if (categoryId === 'all') return TPL_JOURNEYS.length;
  if (categoryId === 'saved') return TPL_JOURNEYS.filter((j) => j.scope === 'org_private').length;
  return TPL_JOURNEYS.filter((j) => j.category === categoryId).length;
}

/** Saved-email count (used by the "My Saved Emails" sub-nav entry). */
export const TPL_EMAIL_SAVED_COUNT = TPL_EMAILS.filter((t) => t.scope === 'org_private').length;

/** Saved-journey count (used by the "My saved sequences" sub-nav entry). */
export const TPL_JOURNEY_SAVED_COUNT = TPL_JOURNEYS.filter((j) => j.scope === 'org_private').length;
